import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { BloomHerLogo } from "@/components/BloomHerLogo";
import { Loader2, Check, Sparkles, Calendar, Clock } from "lucide-react";

const SESSIONS = [
  "Wednesday, 8:00 PM to 10:00 PM ET",
  "Saturday, 8:00 AM to 10:00 AM ET",
  "Saturday, 12:00 PM to 2:00 PM ET",
];

export default function Masterclass() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", session: SESSIONS[0] });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/masterclass").then((r) => setInfo(r.data)).catch(() => setInfo({})); }, []);

  const register = async () => {
    if (!form.name.trim()) { toast.error("Please share your name"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error("Please enter a valid email"); return; }
    setBusy(true);
    try {
      await api.post("/masterclass/register", form);
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not register. Please try again."); }
    finally { setBusy(false); }
  };

  const field = "w-full rounded-xl border border-[#D8C2CD] bg-white px-4 py-3.5 focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine";

  return (
    <div className="min-h-screen bg-cream" data-testid="masterclass-page">
      <div className="bg-wine text-cream">
        <div className="max-w-5xl mx-auto px-6 py-5 flex justify-center">
          <Link to="/"><BloomHerLogo className="h-8 brightness-0 invert" /></Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 lg:py-16 grid lg:grid-cols-2 gap-10 items-start">
        {/* Left: pitch */}
        <div className="animate-fade-up">
          {info?.image_id && (
            <img src={`${API}/media/${info.image_id}`} alt="Bloom Blueprint Masterclass"
              className="w-full rounded-3xl border border-taupe mb-7 shadow-sm" data-testid="mc-hero-image" loading="eager" />
          )}
          <span className="inline-flex items-center gap-2 rounded-full bg-wine/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-wine">
            <Sparkles className="h-3.5 w-3.5" /> Free Masterclass
          </span>
          <h1 className="mt-5 font-serif text-5xl lg:text-6xl leading-[1.05] text-wine">{info?.title || "The Free Health and Wealth Masterclass"}</h1>
          {info?.subtitle && <p className="mt-4 font-serif text-2xl text-[#8B5A73]">{info.subtitle}</p>}
          {info?.description && <p className="mt-5 text-[#6E5862] text-lg leading-relaxed">{info.description}</p>}

          {(info?.date || info?.time) && (
            <div className="mt-6 flex flex-wrap gap-4">
              {info.date && <span className="inline-flex items-center gap-2 text-[#3A1230]"><Calendar className="h-4 w-4 text-wine" /> {info.date}</span>}
              {info.time && <span className="inline-flex items-center gap-2 text-[#3A1230]"><Clock className="h-4 w-4 text-wine" /> {info.time}</span>}
            </div>
          )}

          {info?.perks?.length > 0 && (
            <ul className="mt-7 space-y-3">
              {info.perks.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-[#3A1230]">
                  <span className="mt-0.5 h-6 w-6 rounded-full bg-wine/10 flex items-center justify-center flex-shrink-0"><Check className="h-3.5 w-3.5 text-wine" strokeWidth={3} /></span>
                  {p}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right: registration card */}
        <div className="bg-white rounded-[2rem] border border-taupe p-7 sm:p-9 lg:sticky lg:top-10">
          {done ? (
            <div data-testid="masterclass-success" className="text-center py-6">
              <div className="h-16 w-16 rounded-full bg-wine/10 flex items-center justify-center mx-auto mb-5"><Check className="h-7 w-7 text-wine" strokeWidth={3} /></div>
              <h2 className="font-serif text-3xl text-wine">You're in!</h2>
              <p className="text-[#6E5862] mt-3">Your seat is reserved. Your private webinar link will be sent to you from masterclass.bloomher.health, so keep an eye on your inbox.</p>
              <div className="mt-7 rounded-2xl bg-[#f4c6d9] p-5 text-left">
                <p className="font-serif text-xl text-[#42152F]">Ready for your personalized plan?</p>
                <p className="text-sm text-[#42152F] mt-1.5">Book your 90-minute Bloom Blueprint Session the same day and your $397 applies as full credit toward Radiance or Full Bloom.</p>
                <Link to="/bloom-blueprint-session" data-testid="mc-offer-cta"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#622952] px-6 py-3 text-sm font-medium text-[#FBF9F6] hover:opacity-90 transition-all">
                  Book your Bloom Blueprint Session
                </Link>
              </div>
              <Link to="/" className="mt-5 inline-flex items-center gap-2 text-sm text-wine underline">Explore BloomHer</Link>
            </div>
          ) : (
            <>
              <h2 className="font-serif text-3xl text-[#3A1230]">Reserve your free seat</h2>
              <p className="text-sm text-[#9B7E8D] mt-1.5">Spots are limited. Register below and we'll send your link.</p>
              <div className="mt-6 space-y-4">
                <input data-testid="mc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={field} />
                <input data-testid="mc-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={field} />
                <input data-testid="mc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className={field} />
                <div>
                  <label className="block text-sm font-medium text-[#3A1230] mb-1.5">Choose your session</label>
                  <select data-testid="mc-session" value={form.session} onChange={(e) => setForm({ ...form, session: e.target.value })}
                    className={`${field} appearance-none bg-white cursor-pointer`}>
                    {SESSIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <p className="text-xs text-[#9B7E8D] mt-1.5">Recurring weekly live sessions. Pick the one that fits you best.</p>
                </div>
                <button data-testid="mc-submit" onClick={register} disabled={busy}
                  className="w-full rounded-full bg-wine px-6 py-4 text-sm font-medium text-white hover:bg-wine-dark disabled:opacity-60 transition-all flex items-center justify-center gap-2">
                  {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Reserving your seat</> : <>Save my free seat</>}
                </button>
                <p className="text-center text-xs text-[#9B7E8D]">By registering you agree to receive emails about this masterclass. This is not medical advice.</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
